import { Heading } from '@afterpaytouch/core'
import type { TFunction } from 'i18next'
import React, { type FunctionComponent, memo, type ReactNode } from 'react'

import { useMerchantName, usePostCheckoutExperiment, usePostCheckoutFlow } from '../../../state'
import { usePersonalization } from '../../../utils/personalization'
import { useTranslation } from '../../../utils/convergence'
import { getSpacingClassName } from './helper'
import { MarginSize, type AssetChildComponentSpacing } from './types'

interface Props extends AssetChildComponentSpacing {
  show?: boolean
}

export const Subtitle: FunctionComponent<Props> = memo(({ show = true, margin, marginBottom = MarginSize.x3, marginTop }) => {
  const { t } = useTranslation()
  const merchantDisplayName = useMerchantName() ?? t('postcheckout:asset:merchantDisplayNameDefault')

  const flow = usePostCheckoutFlow()
  const { value: variant } = usePostCheckoutExperiment() ?? {}
  const personalization = usePersonalization()

  const assetId = personalization?.postCheckout?.asset?.id
  const flowSuffix = flow === undefined || flow === null ? '' : `-${String(flow).toUpperCase()}`

  const baseKey = assetId ? `postcheckout:asset:${assetId}.subtitle` : 'postcheckout:asset:subtitle'
  const variantKey = variant ? `${baseKey}__${variant}` : undefined
  const variantFlowKey = variantKey ? `${variantKey}${flowSuffix}` : undefined

  const renderSubtitle = (tFunction: TFunction): ReactNode => {
    if (!show) {
      return null
    }

    const variantFlowText = variantFlowKey ? tFunction(variantFlowKey, '', { merchantDisplayName }) : ''
    const variantText = variantKey ? tFunction(variantKey, '', { merchantDisplayName }) : ''
    const baseText = tFunction(baseKey, '', { merchantDisplayName })

    const hasAnyText = variantFlowText.length > 0 || variantText.length > 0 || baseText.length > 0
    if (!hasAnyText) {
      return null
    }

    return (
      <div className={getSpacingClassName({ margin, marginBottom, marginTop })}>
        <Heading size='M' testNameSpace='post-checkout-subtitle'>
          {variantFlowText}
          {variantText}
          {baseText}
        </Heading>
      </div>
    )
  }

  return <>{renderSubtitle(t)}</>
})
